/**
 * MarkdownPreview — shared markdown renderer with Ur-Nammu prose styling.
 *
 * Single source of truth for rendered legal/markdown content. Used by:
 *   - LegalDocumentCard (publisher accept + public read views)
 *   - AdminLegalEditPage (live preview pane)
 *
 * Flow:
 *   - Wraps `react-markdown` + `remark-gfm` for tables/lists/strikethrough.
 *   - Applies prose-class typography matched to the design system
 *     (Libre Bodoni-friendly headings, Public Sans body, RTL-aware lists).
 *   - Direction inferred from `getEffectiveDir()` unless overridden via `dir`.
 *
 * @layer-1 — shared, no feature imports
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/shared/lib/utils";
import { getEffectiveDir } from "@/shared/lib/direction";

interface MarkdownPreviewProps {
  /** Markdown source string. */
  content: string;
  /** Force a writing direction; otherwise reads document direction. */
  dir?: "ltr" | "rtl";
  /** Optional explicit language attribute (defaults to document lang). */
  lang?: string;
  /** Extra classes applied to the outer prose wrapper. */
  className?: string;
}

/**
 * Renders trusted markdown using react-markdown.
 * Note: `react-markdown` escapes raw HTML by default — safe for admin input.
 */
export function MarkdownPreview({
  content,
  dir,
  lang,
  className,
}: MarkdownPreviewProps) {
  const effectiveDir = dir ?? getEffectiveDir();
  const isRTL = effectiveDir === "rtl";

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none",
        "[&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-0",
        "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-2",
        "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1",
        "[&_p]:mb-3 [&_p]:leading-relaxed",
        "[&_ul]:list-disc [&_ul]:ps-5 [&_ul]:mb-3",
        "[&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:mb-3",
        "[&_li]:mb-1",
        "[&_strong]:font-semibold",
        "[&_a]:underline [&_a]:text-[var(--primary)]",
        "[&_code]:font-mono [&_code]:text-[0.9em] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-[var(--surface-low)]",
        "[&_blockquote]:border-s-2 [&_blockquote]:border-[var(--primary)] [&_blockquote]:ps-3 [&_blockquote]:italic [&_blockquote]:text-[var(--text-secondary)]",
        "[&_table]:w-full [&_table]:border-collapse [&_table]:my-4",
        "[&_th]:text-start [&_th]:font-semibold [&_th]:p-2 [&_th]:border-b",
        "[&_td]:p-2 [&_td]:border-b [&_td]:border-[var(--border)]",
        isRTL && "[&_ul]:pe-5 [&_ol]:pe-5 [&_ul]:ps-0 [&_ol]:ps-0",
        className,
      )}
      dir={effectiveDir}
      lang={lang}
      style={{ color: "var(--text-secondary)" }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}
